"use client";

import { Button } from "@/components/ui/button";
import { APP_DOWNLOAD_URL } from "@/constants/appDownload";
import type { LocaleType } from "@/constants/locales";
import { useIsEmbeddedInApp } from "@/contexts/EmbeddedInAppContext";
import { useIsMobile } from "@/hooks/breakpoints";
import { track } from "@/lib/analytics/client";
import type { Events, ListCtaLocation } from "@/lib/analytics/events";
import { type CountryCode, getLocaleCountry } from "@/utils/location";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";

const CREATE_EVENT_URL = "spond://intercom/create_event";

/** Stays off until a device test shows the app opens the link from its own web view. */
const SHOW_CREATE_EVENT_IN_WEB_VIEW = false;

type TrackingContext = Events["Click list your event store button"];
type Surface = TrackingContext["surface"];
type Store = Events["Click list your event store badge"]["store"];

function Steps() {
  const t = useTranslations("listYourEvent");
  const steps = [
    { key: "download", title: t("stepDownload"), hint: t("stepDownloadHint") },
    { key: "club", title: t("stepCreateClub") },
    { key: "event", title: t("stepCreateEvent") },
  ];

  return (
    <ol className="flex flex-col gap-4">
      {steps.map((step, index) => (
        <li key={step.key} className="flex items-start gap-3">
          <span
            aria-hidden="true"
            className="flex size-7 shrink-0 items-center justify-center rounded-full bg-foreground text-sm font-semibold text-primary-foreground"
          >
            {index + 1}
          </span>
          <div className="pt-0.5">
            <p className="font-medium">{step.title}</p>
            {step.hint && (
              <p className="text-sm text-muted-foreground">{step.hint}</p>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}

function CreateEventButton({ context }: { context: TrackingContext }) {
  const t = useTranslations("listYourEvent");

  return (
    <Button
      asChild
      variant="outline"
      size="full"
      className="h-auto min-h-12 whitespace-normal py-3 text-center"
    >
      <a
        href={CREATE_EVENT_URL}
        onClick={() => track("Click list your event create event", context)}
      >
        {t("createEvent")}
      </a>
    </Button>
  );
}

function PhoneActions({ context }: { context: TrackingContext }) {
  const t = useTranslations("listYourEvent");

  return (
    <div className="flex flex-col gap-3">
      <Button asChild size="full">
        <a
          href={APP_DOWNLOAD_URL}
          onClick={() => track("Click list your event store button", context)}
        >
          {t("getSpond")}
        </a>
      </Button>
      <CreateEventButton context={context} />
    </div>
  );
}

type BadgeImage = { src: string; width: number };

const ENGLISH_BADGES: Record<Store, BadgeImage> = {
  app_store: { src: "/images/email/badge-app-store-dark.png", width: 121 },
  google_play: { src: "/images/email/badge-google-play-dark.png", width: 121 },
};

// Markets without their own badges use the English ones.
const BADGES_BY_MARKET: Partial<
  Record<CountryCode, Record<Store, BadgeImage>>
> = {
  DE: {
    app_store: { src: "/images/badges/app-store-de.svg", width: 120 },
    google_play: { src: "/images/badges/google-play-de.svg", width: 135 },
  },
};

function StoreBadge({
  store,
  image,
  alt,
  context,
}: {
  store: Store;
  image: BadgeImage;
  alt: string;
  context: TrackingContext;
}) {
  return (
    <a
      href={APP_DOWNLOAD_URL}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() =>
        track("Click list your event store badge", { ...context, store })
      }
    >
      <Image
        src={image.src}
        alt={alt}
        width={image.width}
        height={40}
        className="h-10 w-auto"
      />
    </a>
  );
}

function DesktopPanel({ context }: { context: TrackingContext }) {
  const t = useTranslations("listYourEvent");
  const badges = BADGES_BY_MARKET[context.market] ?? ENGLISH_BADGES;

  return (
    <div className="flex shrink-0 flex-col items-center gap-4 rounded-2xl bg-muted p-5 text-center sm:w-56">
      <Image
        src="/images/list-your-event-qr.svg"
        alt={t("qrAlt")}
        width={160}
        height={160}
        className="size-40 rounded-xl"
      />
      <p className="text-sm text-muted-foreground">{t("qrCaption")}</p>
      <div className="flex flex-col items-center gap-2">
        <StoreBadge
          store="app_store"
          image={badges.app_store}
          alt={t("appStoreAlt")}
          context={context}
        />
        <StoreBadge
          store="google_play"
          image={badges.google_play}
          alt={t("googlePlayAlt")}
          context={context}
        />
      </div>
    </div>
  );
}

function useSurface(): Surface {
  const embedded = useIsEmbeddedInApp();
  const isMobile = useIsMobile();
  if (embedded) return "embedded";
  return isMobile ? "phone" : "desktop";
}

export function ListYourEventGuide({
  location,
}: {
  location: ListCtaLocation;
}) {
  const surface = useSurface();
  const market = getLocaleCountry(useLocale() as LocaleType);
  const context: TrackingContext = { location, market, surface };

  if (surface === "embedded") {
    return (
      <div className="flex flex-col gap-6">
        <Steps />
        {SHOW_CREATE_EVENT_IN_WEB_VIEW && (
          <CreateEventButton context={context} />
        )}
      </div>
    );
  }

  if (surface === "phone") {
    return (
      <div className="flex flex-col gap-6">
        <PhoneActions context={context} />
        <Steps />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
      <div className="flex-1">
        <Steps />
      </div>
      <DesktopPanel context={context} />
    </div>
  );
}
